import axiosInstance from '../config/axios';

export interface ExpenseCode {
  id: string;
  account_code: string;
  account_head: string;
  expenditure_details?: string;
  remarks?: string;
  is_approved: boolean;
}

export interface CreateExpenseCodeRequest {
  account_head: string;
  expenditure_details?: string;
  remarks?: string;
}

export interface UpdateExpenseCodeRequest {
  id: string;
  account_head: string;
  expenditure_details?: string;
  remarks?: string;
}

export interface DeleteExpenseCodeRequest {
  id: string;
}

export interface ExpenseCodeResponse {
  data: ExpenseCode[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface GetExpenseCodesRequest {
  page?: number;
  pageSize?: number;
  search?: string;
  includePending?: boolean;
  pendingOnly?: boolean;
  sortField?: string;
  sortOrder?: 'ascend' | 'descend' | undefined;
}

export interface IncomeCode {
  id: string;
  account_code: string;
  account_head: string;
  income_details?: string;
  remarks?: string;
  is_approved: boolean;
}

export interface CreateIncomeCodeRequest {
  account_head: string;
  income_details?: string;
  remarks?: string;
}

export interface UpdateIncomeCodeRequest {
  id: string;
  account_head: string;
  income_details?: string;
  remarks?: string;
}

export interface DeleteIncomeCodeRequest {
  id: string;
}

export interface IncomeCodeResponse {
  data: IncomeCode[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface GetIncomeCodesRequest {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface ProjectCode {
  id: string;
  project_code: string;
  project_type: string;
  is_approved: boolean;
  specific_project_codes?: SpecificProjectCode[];
  project_detail?: string | null;
  delivery_address?: string | null;
  source?: string | null;
  parent_project_id?: string | null;
  parent_project_code?: string | null;
}

export interface CreateProjectCodeRequest {
  project_type: string;
}

export interface UpdateProjectCodeRequest {
  id: string;
  project_type: string;
}

export interface DeleteProjectCodeRequest {
  id: string;
}

export interface ProjectCodeResponse {
  data: ProjectCode[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface GetProjectCodesRequest {
  page?: number;
  pageSize?: number;
  search?: string;
  includeSpecific?: boolean;
  sortOrder?: string;
}

export interface UnifiedCodeItem {
  id: string;
  project_code: string;
  project_type: string;
  type: 'project' | 'specific';
}

export interface UnifiedCodesResponse {
  data: UnifiedCodeItem[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface SpecificProjectCode {
  id: string;
  specific_code: string;
  project_detail: string;
  is_approved: boolean;
}

export interface SpecificProjectCodeWithParent extends SpecificProjectCode {
  project_code_id: string;
  parent_project_code: string;
  parent_project_type: string;
  parent_is_approved: boolean;
}

export interface SpecificProjectCodeSummary extends SpecificProjectCode {
  project_code_id: string;
  parent_project_code: string | null;
  parent_project_type: string | null;
}

export interface CreateSpecificProjectCodeRequest {
  projectCode: string;
  projectCodeId: string;
  projectDetail: string;
}

export interface UpdateSpecificProjectCodeRequest {
  id: string;
  project_detail: string;
}

export interface DeleteSpecificProjectCodeRequest {
  id: string;
}

export interface PartyCode {
  id: string;
  party_code: string;
  company_name: string;
  company_gst_no?: string;
  is_approved?: boolean;
  is_reviewed?: boolean;
}

export interface PartyCodeResponse {
  data: PartyCode[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface GetPartyCodesRequest {
  page?: number;
  pageSize?: number;
  search?: string;
  pendingApproval?: boolean;
  sortField?: string;
  sortOrder?: string;
}

export interface CreatePartyCodeRequest {
  company_name: string;
  company_gst_no: string;
  address: string,
  contact_person_name: string,
  contact_person_email: string,
  contact_phone_cc: string,
  contact_person_phone: string,
  company_landline?: string,
  company_website?: string,
  bank_name: string,
  bank_account_number: string,
  bank_ifsc_code: string,
  bank_micr_code?: string,
  company_pan: string,
  company_gst_number: string,
  company_inc_number: string,
  panCard?: File;
  gstCertificate?: File;
  cancelledcheque?: File;
  incorporationCertificate?: File;
}

export interface UpdatePartyCodeRequest {
  id: string;
  company_name: string;
  company_gst_no: string;
  address: string,
  contact_person_name: string,
  contact_person_email: string,
  contact_phone_cc: string,
  contact_person_phone: string,
  company_landline?: string,
  company_website?: string,
  bank_name: string,
  bank_account_number: string,
  bank_ifsc_code: string,
  bank_micr_code?: string,
  company_pan: string,
  company_gst_number: string,
  company_inc_number: string,
  isApproved: boolean,
  panCard?: File;
  gstCertificate?: File;
  cancelledcheque?: File;
  incorporationCertificate?: File;
}

export interface DeletePartyCodeRequest {
  id: string;
}

export interface UpdatePartyCodeResponse {
  message: string;
  fieldsUpdated?: number;
  documentsProcessed?: number;
  documentDetails?: Array<{
    documentType: string;
    filename: string;
    size: number;
    objectName: string;
    action: 'created' | 'updated';
  }>;
}

export interface SpecificProjectCodeResponse {
  data: SpecificProjectCode[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface GetSpecificProjectCodesRequest {
  page?: number;
  pageSize?: number;
  search?: string;
  sortOrder?: string;
}

export interface SpecificProjectCodeSummaryResponse {
  data: SpecificProjectCodeSummary[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface GenerateLinkResponse {
  success: boolean;
  url: string;
}

export interface PartyLinkRequestResponse {
  success: boolean;
  message: string;
}

export interface PartyLinkApprovalResponse {
  success: boolean;
  message: string;
  url?: string;
}

export interface POTermsAndConditionsItem {
  id: string;
  termsAndConditions: string;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdByName?: string | null;
  updatedByName?: string | null;
}

export interface CreatePOTermsAndConditionsRequest {
  termsAndConditions: string;
}

export interface UpdatePOTermsAndConditionsRequest {
  id: string;
  termsAndConditions: string;
}

class PurchaseService {
  async getExpenseCodes(params: GetExpenseCodesRequest): Promise<ExpenseCodeResponse> {
    const { page = 1, pageSize = 10, search = '', includePending = false, pendingOnly = false, sortField, sortOrder } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search }),
      ...(pendingOnly ? { pendingOnly: 'true' } : includePending ? { includePending: 'true' } : {}),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder })
    });

    const response = await axiosInstance.get(`/codevault/expense-code?${queryParams}`);
    return response.data;
  }

  async addExpenseCode(data: CreateExpenseCodeRequest): Promise<{ message: string; id?: string; account_code?: string }> {
    const response = await axiosInstance.post('/codevault/expense-code/add', data);
    return response.data;
  }

  async updateExpenseCode(data: UpdateExpenseCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/expense-code/update', data);
    return response.data;
  }

  async deleteExpenseCode(data: DeleteExpenseCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/expense-code/delete', data);
    return response.data;
  }

  async approveExpenseCode(id: string): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/expense-code/approve', { id });
    return response.data;
  }

  // Income Code Methods
  async getIncomeCodes(params: GetIncomeCodesRequest): Promise<IncomeCodeResponse> {
    const { page = 1, pageSize = 10, search = '' } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search })
    });

    const response = await axiosInstance.get(`/codevault/income-code?${queryParams}`);
    return response.data;
  }

  async addIncomeCode(data: CreateIncomeCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/income-code/add', data);
    return response.data;
  }

  async updateIncomeCode(data: UpdateIncomeCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/income-code/update', data);
    return response.data;
  }

  async deleteIncomeCode(data: DeleteIncomeCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/income-code/delete', data);
    return response.data;
  }

  async approveIncomeCode(id: string): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/income-code/approve', { id });
    return response.data;
  }

  // Project Code Methods
  /**
   * Fetches project codes from the codevault API.
   * Does NOT require purchase order access — any authenticated user with
   * project-code read access can call this endpoint.
   */
  async getAllCodesUnified(params: { page?: number; pageSize?: number; search?: string; sortOrder?: string }): Promise<UnifiedCodesResponse> {
    const { page = 1, pageSize = 50, search = '', sortOrder = '' } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search }),
      ...(sortOrder && { sortOrder })
    });
    const response = await axiosInstance.get(`/codevault/all-codes?${queryParams}`);
    return response.data;
  }

  async getProjectCodesForPage(params: GetProjectCodesRequest): Promise<ProjectCodeResponse> {
    const { page = 1, pageSize = 10, search = '', sortOrder = '' } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search }),
      ...(sortOrder && { sortOrder })
    });

    const response = await axiosInstance.get(`/codevault/project-code?${queryParams}`);
    const responseData = response.data;

    if (responseData && Array.isArray(responseData.data) && responseData.pagination) {
      const normalizedData: ProjectCode[] = responseData.data
        .map((item: any) => ({
          id: item.id || '',
          project_code: item.project_code ?? '',
          project_type: item.project_type ?? '',
          is_approved: item.is_approved !== undefined ? item.is_approved : false,
          specific_project_codes: item.specific_project_codes,
          project_detail: item.project_detail ?? null,
          delivery_address: item.delivery_address ?? null,
          source: item.source ?? null,
          parent_project_id: item.parent_project_id ?? null,
          parent_project_code: item.parent_project_code ?? null,
        }))
        .filter((item: ProjectCode) => Boolean(item.id));

      return {
        data: normalizedData,
        pagination: responseData.pagination,
      };
    }

    return responseData;
  }

  async getProjectCodes(params: GetProjectCodesRequest): Promise<ProjectCodeResponse> {
    const { page = 1, pageSize = 10, search = '', includeSpecific = true } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search })
    });
    if (includeSpecific === false) {
      queryParams.set('includeSpecific', 'false');
    }

    const response = await axiosInstance.get(`/purchase/project-codes?${queryParams}`);
    const responseData = response.data;

    if (responseData && Array.isArray(responseData.data) && responseData.pagination) {
      const normalizedData: ProjectCode[] = responseData.data
        .map((item: any) => ({
          id: item.projectId || item.id || item.project_id || '',
          project_code: item.projectCode ?? item.project_code ?? item.code ?? '',
          project_type: item.projectType ?? item.project_type ?? '',
          is_approved:
            item.isApproved !== undefined
              ? item.isApproved
              : item.is_approved !== undefined
                ? item.is_approved
                : false,
          specific_project_codes: item.specific_project_codes || item.specificProjectCodes,
          project_detail: item.projectDetail ?? item.project_detail ?? null,
          delivery_address: item.deliveryAddress ?? item.delivery_address ?? null,
          source: item.source ?? item.record_source ?? null,
          parent_project_id: item.parentProjectId ?? item.parent_project_id ?? null,
          parent_project_code: item.parentProjectCode ?? item.parent_project_code ?? null,
        }))
        .filter((item: ProjectCode) => Boolean(item.id));

      return {
        data: normalizedData,
        pagination: responseData.pagination,
      };
    }

    return responseData;
  }

  async addProjectCode(data: CreateProjectCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/project-code/add', data);
    return response.data;
  }

  async updateProjectCode(data: UpdateProjectCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/project-code/update', data);
    return response.data;
  }

  async deleteProjectCode(data: DeleteProjectCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/project-code/delete', data);
    return response.data;
  }

  async approveProjectCode(id: string): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/project-code/approve', { id });
    return response.data;
  }
  // Party Code Methods
  async getPartyCodes(params: GetPartyCodesRequest): Promise<PartyCodeResponse> {
    const { page = 1, pageSize = 10, search = '', pendingApproval = false, sortField = '', sortOrder = '' } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search }),
      ...(pendingApproval && { pendingApproval: 'true' }),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder })
    });

    const response = await axiosInstance.get(`/codevault/party-code?${queryParams}`);
    return response.data;
  }

  async addPartyCode(data: CreatePartyCodeRequest): Promise<{ message: string }> {
    const formData = new FormData();
    formData.append('companyname', data.company_name);
    formData.append('isClient', 'false');
    if (data.company_gst_no) {
      formData.append('company_gst_no', data.company_gst_no);
    }
    formData.append('address', data.address);
    formData.append('contact_person_name', data.contact_person_name);
    formData.append('contact_person_email', data.contact_person_email);
    formData.append('contact_phone_cc', data.contact_phone_cc);
    formData.append('contact_person_phone', data.contact_person_phone);

    if (data.company_landline) {
      formData.append('company_landline', data.company_landline);
    }

    if (data.company_website) {
      formData.append('company_website', data.company_website);
    }

    formData.append('bank_name', data.bank_name);
    formData.append('bank_account_number', data.bank_account_number);
    formData.append('bank_ifsc_code', data.bank_ifsc_code);

    if (data.bank_micr_code) {
      formData.append('bank_micr_code', data.bank_micr_code);
    }

    if (data.company_pan) {
      formData.append('company_pan', data.company_pan);
    }
    if (data.company_inc_number) {
      formData.append('company_inc_no', data.company_inc_number);
    }

    if (data.panCard) {
      formData.append('panCard', data.panCard);
    }

    if (data.gstCertificate) {
      formData.append('gstCertificate', data.gstCertificate);
    }

    if (data.cancelledcheque) {
      formData.append('cancelledcheque', data.cancelledcheque);
    }

    if (data.incorporationCertificate) {
      formData.append('incorporationCertificate', data.incorporationCertificate);
    }

    const response = await axiosInstance.post('/codevault/party-code/add', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updatePartyCode(data: UpdatePartyCodeRequest): Promise<UpdatePartyCodeResponse> {
    const formData = new FormData();
    formData.append('id', data.id);
    formData.append('companyname', data.company_name);
    if (data.company_gst_no) {
      formData.append('company_gst_no', data.company_gst_no);
    }
    formData.append('address', data.address);
    formData.append('contact_person_name', data.contact_person_name);
    formData.append('contact_person_email', data.contact_person_email);
    formData.append('contact_phone_cc', data.contact_phone_cc);
    formData.append('contact_person_phone', data.contact_person_phone);

    if (data.company_landline) {
      formData.append('company_landline', data.company_landline);
    }

    if (data.company_website) {
      formData.append('company_website', data.company_website);
    }

    formData.append('bank_name', data.bank_name);
    formData.append('bank_account_number', data.bank_account_number);
    formData.append('bank_ifsc_code', data.bank_ifsc_code);

    if (data.bank_micr_code) {
      formData.append('bank_micr_code', data.bank_micr_code);
    }

    if (data.company_pan) {
      formData.append('company_pan', data.company_pan);
    }
    if (data.company_inc_number) {
      formData.append('company_inc_no', data.company_inc_number);
    }
    formData.append('isApproved', data.isApproved.toString());

    // Only append files if they exist (new files to upload)
    if (data.panCard) {
      formData.append('panCard', data.panCard);
    }

    if (data.gstCertificate) {
      formData.append('gstCertificate', data.gstCertificate);
    }

    if (data.cancelledcheque) {
      formData.append('cancelledcheque', data.cancelledcheque);
    }

    if (data.incorporationCertificate) {
      formData.append('incorporationCertificate', data.incorporationCertificate);
    }

    const response = await axiosInstance.post('/codevault/party-code/update', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deletePartyCode(data: DeletePartyCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/party-code/delete', data);
    return response.data;
  }

  async reviewPartyCode(id: string, review_notes?: string): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/party-code/review', { id, review_notes });
    return response.data;
  }

  async approvePartyCode(id: string, approval_notes?: string): Promise<{ message: string; partyCode?: string; partyCodeId?: string }> {
    const response = await axiosInstance.post('/codevault/party-code/approve', { id, approval_notes });
    return response.data;
  }

  async rejectPartyCode(id: string, rejection_reason: string): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/party-code/reject', { id, rejection_reason });
    return response.data;
  }

  async getSpecificProjectCodes(projectCodeId: string, params: GetSpecificProjectCodesRequest): Promise<SpecificProjectCodeResponse> {
    const { page = 1, pageSize = 10, search = '' } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search })
    });

    const response = await axiosInstance.get(`/codevault/specific-project-code/${projectCodeId}?${queryParams}`);
    return response.data;
  }

  async getApprovedSpecificProjectCodes(params: GetSpecificProjectCodesRequest): Promise<SpecificProjectCodeSummaryResponse> {
    const { page = 1, pageSize = 10, search = '', sortOrder = '' } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(search && { search }),
      ...(sortOrder && { sortOrder })
    });

    const response = await axiosInstance.get(`/codevault/specific-project-code?${queryParams}`);
    return response.data;
  }

  async addSpecificProjectCode(data: CreateSpecificProjectCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/specific-project-code/add', data);
    return response.data;
  }

  async updateSpecificProjectCode(data: UpdateSpecificProjectCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/specific-project-code/update', data);
    return response.data;
  }

  async deleteSpecificProjectCode(data: DeleteSpecificProjectCodeRequest): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/specific-project-code/delete', data);
    return response.data;
  }

  async approveSpecificProjectCode(id: string): Promise<{ message: string }> {
    const response = await axiosInstance.post('/codevault/specific-project-code/approve', { id });
    return response.data;
  }

  async getSpecificProjectCodeById(id: string): Promise<{ data: SpecificProjectCodeWithParent }> {
    const response = await axiosInstance.get(`/codevault/specific-project-code/detail/${id}`);
    return response.data;
  }

  async getPartyDetails(id: string, isApproved: boolean): Promise<any> {
    const response = await axiosInstance.get(`/codevault/party-code/getPartyDetails/${id}/${isApproved}`);
    return response.data;
  }

  async previewPartyDocument(partyId: string, documentType: string, isApproved: boolean): Promise<Blob> {
    const response = await axiosInstance.get(
      `/codevault/party-code/preview-document/${partyId}/${documentType}/${isApproved}`,
      {
        responseType: 'blob',
        timeout: 30000,
      }
    );
    return response.data;
  }

  async getPartyDocumentPreviewUrl(partyId: string, documentType: string, isApproved: boolean): Promise<string> {
    const baseUrl = axiosInstance.defaults.baseURL || '';
    return `${baseUrl}/codevault/party-code/preview-document/${partyId}/${documentType}/${isApproved}`;
  }

  getPartyDocumentPreviewUrlSync(partyId: string, documentType: string, isApproved: boolean): string {
    const baseUrl = axiosInstance.defaults.baseURL || '';
    return `${baseUrl}/codevault/party-code/preview-document/${partyId}/${documentType}/${isApproved}`;
  }

  async generatePartyRegistrationLink(): Promise<GenerateLinkResponse> {
    const response = await axiosInstance.post('/codevault/party-code/generate-link');
    return response.data;
  }

  async listPartyRegistrationLinkRequests(page = 1, pageSize = 10): Promise<{ data: any[]; pagination: { page: number; pageSize: number; total: number } }> {
    const response = await axiosInstance.get('/codevault/party-code/link-requests', { params: { page, pageSize } });
    return response.data;
  }

  async listGeneratedPartyLinks(page = 1, pageSize = 10, isOptional?: boolean, status?: string): Promise<{ data: any[]; pagination: { page: number; pageSize: number; total: number } }> {
    const response = await axiosInstance.get('/codevault/party-code/generated-links', { params: { page, pageSize, ...(isOptional !== undefined && { isOptional }), ...(status && { status }) } });
    return response.data;
  }

  async approvePartyRegistrationLinkRequestById(id: string): Promise<{ success?: boolean; message: string; url?: string }> {
    const response = await axiosInstance.post('/codevault/party-code/approve-request', { id });
    return response.data;
  }

  async getPurchaseOrderApprovers(): Promise<{ data: Array<{ userId: string; email: string; name: string }> }> {
    const response = await axiosInstance.get('/codevault/party-code/po-approvers');
    return response.data;
  }

  async requestPartyRegistrationLinkApproval(message: string): Promise<PartyLinkRequestResponse> {
    const response = await axiosInstance.post('/codevault/party-code/request-link', { message });
    return response.data;
  }

  async validatePartyRegistrationToken(token: string): Promise<{ success: boolean; optional?: boolean }> {
    const response = await axiosInstance.post('/open-vendor/party-code/validate-token', {
      token,
      url: window.location.href
    });
    return response.data;
  }

  async approvePartyRegistrationLink(token: string): Promise<PartyLinkApprovalResponse> {
    const response = await axiosInstance.post('/open-vendor/party-code/approve-link', {
      token
    });
    return response.data;
  }

  // Public (open vendor) Party Registration Insert
  async insertPartyRegistration(formData: FormData): Promise<{ success: boolean }> {
    // Do NOT set Content-Type manually; axios will handle multipart boundary
    const response = await axiosInstance.post('/open-vendor/party-code/insert', formData, {
      timeout: 60000
    });
    return response.data;
  }

  async listPOTermsAndConditions(): Promise<POTermsAndConditionsItem[]> {
    const response = await axiosInstance.get<{ success: boolean; data: POTermsAndConditionsItem[] }>('/purchase/terms-and-conditions');
    return response.data.data;
  }

  async addPOTermsAndConditions(data: CreatePOTermsAndConditionsRequest): Promise<{ id: string }> {
    const response = await axiosInstance.post<{ success: boolean; data: { id: string } }>('/purchase/terms-and-conditions', data);
    return response.data.data;
  }

  async updatePOTermsAndConditions(data: UpdatePOTermsAndConditionsRequest): Promise<{ message: string }> {
    const response = await axiosInstance.put<{ success: boolean; message: string }>(`/purchase/terms-and-conditions/${data.id}`, {
      termsAndConditions: data.termsAndConditions,
    });
    return response.data;
  }

  async deletePOTermsAndConditions(id: string): Promise<{ message: string }> {
    const response = await axiosInstance.delete<{ success: boolean; message: string }>(`/purchase/terms-and-conditions/${id}`);
    return response.data;
  }
}

const purchaseService = new PurchaseService();
export default purchaseService;
